/**
 * The trader, the fabricator, and the vault.
 *
 * Three things a station does with your money, in one panel because they are
 * the same errand: you come here with linelits and leave with line.
 *
 * The vault is first, deliberately. Banking is the thing you should do before
 * flying off with everything you own, and putting it behind the shopping would
 * make it the thing you remember afterwards.
 */

import {
  DEFAULT_LINE_COLOR,
  COMPONENT_SPECS,
  FABRICATOR_MAX_LENGTH,
  canAfford,
  deposit,
  fabricate,
  fabricationCost,
  formatLinelits,
  linePrice,
  cargoWeight,
  spend,
  stow,
  traderStock,
  unitWeight,
  withdraw,
  type CargoItem,
  type TraderItem,
  type Wallet,
  bankingCap,
  type ShipGraph,
} from '@interspacial/shared';
import { useEffect, useMemo, useState } from 'react';

/**
 * The smallest linelit that can actually be stored.
 *
 * Balances are `NUMERIC(20,3)`, so anything under this is a rounding artefact
 * rather than money — and a vault "full" to within a thousandth is full. The
 * same number the bank action tests against, for the same reason.
 */
const STORABLE = 0.001;

export interface StationShopProps {
  seed: string;
  stationId: string;
  wallet: Wallet;
  /** The ship, because its worth is what the vault will hold. */
  hull: ShipGraph;
  onWalletChange: (wallet: Wallet) => void;
  cargo: readonly CargoItem[];
  onCargoChange: (cargo: CargoItem[]) => void;
  /** Hold space, in weight. The sum of the hull's vaults. */
  capacity: number;
  /** How much of this shelf everyone has taken this window, by item id. */
  taken: Record<string, number>;
  /**
   * Ask the server to do something.
   *
   * Buying, laying line and banking are all decisions the server makes: it
   * re-derives the price from the same functions used to draw it here, checks
   * the shelf, and answers with the record. Nothing below changes anything
   * locally — it asks, and the answer arrives as new props.
   */
  act: (path: string, body?: Record<string, unknown>) => Promise<boolean>;
  /**
   * A feature has been used, by name. See `lessons.ts`.
   *
   * The vault is the one panel here with a rule nobody can see coming: the
   * ceiling is the ship, and the first anybody hears of it is a deposit that
   * moved less than they asked.
   */
  onFeatureUsed?: (lessonId: string) => void;
}

type Tab = 'vault' | 'trader';

export function StationShop({
  seed,
  stationId,
  wallet,
  hull,
  onWalletChange,
  cargo,
  onCargoChange,
  capacity,
  taken,
  act,
  onFeatureUsed,
}: StationShopProps) {
  /*
   * The trader, not the vault.
   *
   * A shop opens on the thing people came to do. Banking is a deliberate errand
   * — you go to it — and having it in front by default meant every visit began
   * on the one panel that does not sell anything.
   */
  const [tab, setTab] = useState<Tab>('trader');

  /*
   * What to move, as typed.
   *
   * Kept as a string so the field can be empty and mid-edit without the number
   * snapping to zero under the cursor. Read once, on the way out.
   */
  const [amount, setAmount] = useState('');
  const moving = Math.max(0, Number(amount) || 0);

  /*
   * The ceiling, which is the ship.
   *
   * Computed here from the same functions the server decides with, so the panel
   * shows the rule rather than an approximation of it — and a deposit that
   * would be clipped is visibly clipped before it is sent.
   */
  const vaultCap = bankingCap(hull, cargo);
  const room = Math.max(0, vaultCap - wallet.banked);
  const [note, setNote] = useState<string | null>(null);

  /*
   * The ceiling, explained the first time it actually binds.
   *
   * Keyed on "will not all fit" rather than on the deposit button, because the
   * button is *disabled* when the vault is full — which is the case most worth
   * teaching, and the one where a player is left pressing something that does
   * nothing. Carrying more than the room left means the next deposit is clipped
   * whether or not it is ever pressed, and that is the fact the lesson is about.
   */
  useEffect(() => {
    if (tab !== 'vault') return;
    if (wallet.carried <= STORABLE) return;
    if (wallet.carried <= room + STORABLE) return;
    onFeatureUsed?.('banking-cap');
  }, [tab, wallet.carried, room, onFeatureUsed]);

  // Seeded off the station, so the shelf is the same one you found last time.
  const shelf = useMemo(() => traderStock(seed, stationId), [seed, stationId]);

  /*
   * What has been taken off this shelf, this restocking period.
   *
   * The stock list is generated from the seed, so it was the same list every
   * time it was read — buying showed a quantity, charged for it, and left the
   * quantity where it was. You could clear a trader out of warp cores and the
   * shelf would still say three.
   *
   * Carried in the save against a period number, so it survives undocking and
   * comes back on its own two hours later. See `shelfEra`.
   */
  const stock = useMemo(
    () => shelf.map((item) => ({ ...item, quantity: item.quantity - (taken[item.id] ?? 0) })),
    [shelf, taken],
  );

  const used = cargoWeight(cargo);

  const buy = (item: TraderItem) => {
    // Priced and stock-checked here only to decide what to draw and whether to
    // bother asking. The server does all of it again and its answer is the one
    // that counts — see `runAction`.
    if (item.quantity <= 0) {
      setNote(`${describeItem(item)} — none left.`);
      return;
    }

    void act('/trade/buy', { itemId: item.id }).then((ok) => {
      if (ok) setNote(`Bought ${describeItem(item)} for ${formatLinelits(item.price)}L.`);
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 300 }}>
      {/* Purse and hold are in the panel headers now — see `Readout` in
          `DockedView`. Repeating them here put the same three numbers on screen
          twice, a hand's width apart. */}
      <div style={{ display: 'flex', gap: 6 }}>
        {(['vault', 'trader'] as const).map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => {
              setTab(option);
              setNote(null);
            }}
            style={tabStyle(option === tab)}
          >
            {option}
          </button>
        ))}
      </div>

      {tab === 'vault' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={hint}>
            What is on the ship goes with the ship. What is banked is reachable from any station.
          </div>
          {/* How much the vault will hold.
              Worth saying plainly rather than only when a deposit is refused:
              the ceiling is the ship's own worth, so it is a fact about the hull
              you are standing in rather than a limit somebody imposed. */}
          <div style={hint}>
            This vault holds {formatLinelits(vaultCap)}L — as much as the ship is worth.
            {room <= 0 && ' It is full: build or buy more ship to bank more.'}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              type="number"
              min={0}
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              placeholder="amount"
              style={{
                flex: 1,
                minWidth: 0,
                background: 'rgba(0,0,0,0.35)',
                border: '1px solid rgba(127,212,255,0.22)',
                color: '#dbeaff',
                font: 'inherit',
                fontSize: 12,
                padding: '4px 6px',
              }}
            />
            {/* The two ends, because "all of it" is the common case and typing a
                balance out is a chore. Everything between is the field. */}
            <button
              type="button"
              onClick={() => setAmount(String(Math.min(wallet.carried, room)))}
              style={{ ...actionStyle, width: 'auto', padding: '4px 8px' }}
              title="As much as will fit"
            >
              max in
            </button>
            <button
              type="button"
              onClick={() => setAmount(String(wallet.banked))}
              style={{ ...actionStyle, width: 'auto', padding: '4px 8px' }}
              title="Everything banked"
            >
              max out
            </button>
          </div>

          <div style={{ display: 'flex', gap: 6 }}>
            <button
              type="button"
              onClick={() => {
                void act('/bank', { direction: 'deposit', amount: moving });
                setAmount('');
              }}
              disabled={moving <= 0 || wallet.carried <= 0 || room <= 0}
              style={actionStyle}
            >
              deposit
            </button>
            <button
              type="button"
              onClick={() => {
                void act('/bank', { direction: 'withdraw', amount: moving });
                setAmount('');
              }}
              disabled={moving <= 0 || wallet.banked <= 0}
              style={actionStyle}
            >
              withdraw
            </button>
          </div>
        </div>
      )}

      {/* The trader's stock, at its full height.

          It used to cap at 260 and scroll inside a counter that also scrolls —
          two scrollbars an inch apart, and on a phone no way to tell which one a
          drag was about to move. The counter is the scroller; this is a list in
          it. */}
      {tab === 'trader' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {stock.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => buy(item)}
              disabled={!canAfford(wallet, item.price) || item.quantity <= 0}
              style={{
                ...rowStyle,
                opacity: canAfford(wallet, item.price) && item.quantity > 0 ? 1 : 0.4,
                cursor:
                  canAfford(wallet, item.price) && item.quantity > 0 ? 'pointer' : 'not-allowed',
              }}
            >
              <span style={{ flex: 1, textAlign: 'left' }}>{describeItem(item)}</span>
              <span style={{ opacity: 0.35, width: 44, textAlign: 'right', fontSize: 10.5 }}>
                {unitWeight(cargoEntry(item)).toFixed(2)}w
              </span>
              <span style={{ opacity: 0.45, width: 26, textAlign: 'right' }}>×{item.quantity}</span>
              <span style={{ width: 56, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {formatLinelits(item.price)}L
              </span>
            </button>
          ))}
        </div>
      )}

      {note && <div style={{ ...hint, color: '#ffb347' }}>{note}</div>}
    </div>
  );
}

/**
 * A shelf entry, as it would sit in the hold.
 *
 * Built before the money moves so the weight can be checked — a purchase that
 * charges you and then has nowhere to go is worse than one that is refused.
 */
function cargoEntry(item: TraderItem): CargoItem {
  if (item.kind === 'line') {
    return {
      id: `line-${item.length}-${item.strength}`,
      kind: 'line',
      label: `${item.length}u line`,
      length: item.length,
      strength: item.strength,
      color: DEFAULT_LINE_COLOR,
      count: 1,
    };
  }

  if (item.kind === 'joint') {
    return { id: 'joint', kind: 'joint', label: 'joint', count: 1 };
  }

  // Consumables carry their own weight — there is no component spec or length
  // to derive it from, which is exactly what the `resource` kind is for.
  if (item.kind === 'resource') {
    return {
      id: item.id,
      kind: 'resource',
      label: item.label,
      weight: item.weight,
      // What it sells back for. A resource has nothing else to derive a value
      // from, and without this an overdrive core — a hundred and fifty linelits
      // of merchandise — read as worthless in the hold beside a delivery crate.
      value: item.price,
      count: 1,
    };
  }

  /*
   * A curio, priced by this station and remembering what it cost.
   *
   * `paid` is the whole reason the hold can ever show a profit: a trinket has no
   * worth of its own, so "is this a good price" only means something against
   * what was paid for it three systems ago.
   */
  if (item.kind === 'trinket') {
    return {
      id: `${item.id}#${item.price}`,
      trinket: item.id,
      kind: 'trinket',
      label: item.label,
      weight: item.weight,
      value: item.base,
      paid: item.price,
      count: 1,
    };
  }

  return {
    id: `component-${item.type}`,
    kind: 'component',
    label: COMPONENT_SPECS[item.type].name,
    type: item.type,
    count: 1,
  };
}

/** What a shelf entry is called. Joints have nothing but a name. */
function describeItem(item: TraderItem): string {
  if (item.kind === 'line') return `${item.length}u line`;
  if (item.kind === 'joint') return 'joint';
  if (item.kind === 'resource') return item.label;
  if (item.kind === 'trinket') return item.label;
  return COMPONENT_SPECS[item.type].name;
}

const tabStyle = (active: boolean): React.CSSProperties => ({
  flex: 1,
  padding: '5px 0',
  background: active ? 'rgba(127,212,255,0.18)' : 'transparent',
  border: '1px solid rgba(127,212,255,0.22)',
  color: active ? '#dbeaff' : 'rgba(219,234,255,0.6)',
  font: 'inherit',
  fontSize: 11,
  letterSpacing: 0.8,
  textTransform: 'uppercase',
  cursor: 'pointer',
});

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '5px 8px',
  background: 'rgba(255,255,255,0.03)',
  border: '1px solid rgba(127,212,255,0.14)',
  color: '#dbeaff',
  font: 'inherit',
  fontSize: 12,
};

const actionStyle: React.CSSProperties = {
  flex: 1,
  padding: '6px 12px',
  background: 'rgba(8,14,24,0.82)',
  border: '1px solid rgba(127,212,255,0.22)',
  color: '#dbeaff',
  font: 'inherit',
  fontSize: 12,
  cursor: 'pointer',
};

const hint: React.CSSProperties = {
  fontSize: 11,
  opacity: 0.55,
  lineHeight: 1.45,
};
